import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { GLYPH } from './glyphs';

/**
 * Модальное окно (README «Оверлеи»): подложка rgba(26,22,20,.42); ширина 520–820 (по умолчанию 560),
 * max-width calc(100vw − 96px), max-height calc(100vh − 72px); radius 18, border line-strong, тень окна,
 * появление lwRise 200 ms. Шапка: надзаголовок 11/500 ink-400, заголовок Display 24/28; крестик 28×28.
 * Тело скроллится (padding 16 20); подвал — пояснение и кнопки на paper-base.
 * Закрытие — крестик, Esc или клик по подложке.
 */
export const DIALOG_WIDTHS = [520, 560, 600, 620, 640, 680, 720, 760, 820, 860] as const;
export type DialogWidth = (typeof DIALOG_WIDTHS)[number];

const WIDTH_CLASSES: Readonly<Record<DialogWidth, string>> = {
  520: 'w-520',
  560: 'w-560',
  600: 'w-600',
  620: 'w-620',
  640: 'w-640',
  680: 'w-680',
  720: 'w-720',
  760: 'w-760',
  820: 'w-820',
  860: 'w-860',
};

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogContentProps {
  width?: DialogWidth;
  children: ReactNode;
  className?: string;
  /** Блокировать закрытие кликом по подложке/Esc (например, во время отправки). */
  preventClose?: boolean;
  /** В окне нет вводного текста (DialogLead) — не связывать описание. */
  noDescription?: boolean;
}

export function DialogContent({
  width = 560,
  children,
  className,
  preventClose = false,
  noDescription = false,
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 grid place-items-center overflow-hidden bg-scrim-modal">
        <DialogPrimitive.Content
          {...(noDescription ? { 'aria-describedby': undefined } : {})}
          onEscapeKeyDown={(event) => (preventClose ? event.preventDefault() : undefined)}
          onPointerDownOutside={(event) => (preventClose ? event.preventDefault() : undefined)}
          className={cn(
            'flex max-h-[calc(100vh-72px)] max-w-[calc(100vw-96px)] flex-col overflow-hidden rounded-18 border border-line-strong bg-paper-pure shadow-modal animate-rise focus-visible:outline-none',
            WIDTH_CLASSES[width],
            className,
          )}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
}

export interface DialogHeaderProps {
  overline?: ReactNode;
  title: ReactNode;
  /** Скрыть крестик (например, пока идёт отправка). */
  hideClose?: boolean;
}

export function DialogHeader({ overline, title, hideClose = false }: DialogHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 items-start gap-16 border-b border-line-hair px-20 pt-16 pb-14">
      <div className="flex min-w-0 flex-col gap-2">
        {overline ? (
          <span className="text-[11px] font-medium tracking-[0.04em] text-ink-400">{overline}</span>
        ) : null}
        <DialogPrimitive.Title className="font-display text-[24px] leading-[28px] tracking-[-0.4px] text-pretty text-ink-900">
          {title}
        </DialogPrimitive.Title>
      </div>
      <span className="flex-1" />
      {hideClose ? null : (
        <DialogPrimitive.Close
          aria-label={t('actions.close')}
          className="flex size-28 shrink-0 cursor-pointer items-center justify-center rounded-16 text-[16px] text-ink-500 hover:bg-surface-sunken"
        >
          <span aria-hidden="true">{GLYPH.close}</span>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

export function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-h-0 flex-1 overflow-y-auto px-20 py-16', className)}>{children}</div>
  );
}

/** Вводный текст окна: 12/18 ink-500. */
export function DialogLead({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <DialogPrimitive.Description
      id={id}
      className="mb-14 text-[12px] leading-[18px] text-pretty text-ink-500"
    >
      {children}
    </DialogPrimitive.Description>
  );
}

/** Плашка ошибки формы: фон accent-wash (#F8E7E8), точка и текст status-error 12/18. */
export function FormAlert({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      role="alert"
      className={cn(
        'mb-16 flex items-start gap-10 rounded-8 bg-accent-wash px-14 py-12',
        className,
      )}
    >
      <span aria-hidden="true" className="mt-6 size-6 shrink-0 rounded-pill bg-status-error" />
      <span className="text-[12px] leading-[18px] text-pretty text-status-error">{children}</span>
    </div>
  );
}

/** Примечание окна: фон gold-wash-2, текст 12/18 gold-leaf-3. */
export function DialogNote({ children }: { children: ReactNode }) {
  return (
    <div className="mt-12 rounded-16 bg-gold-wash-2 px-12 py-10">
      <span className="text-[12px] leading-[18px] text-gold-leaf-3">{children}</span>
    </div>
  );
}

export interface DialogFooterProps {
  note?: ReactNode;
  children: ReactNode;
}

export function DialogFooter({ note, children }: DialogFooterProps) {
  return (
    <div className="flex shrink-0 items-center gap-8 border-t border-line-hair bg-paper-base px-20 py-12">
      {note ? (
        <span className="min-w-0 text-[12px] leading-[18px] text-pretty text-ink-500">{note}</span>
      ) : null}
      <span className="flex-1" />
      {children}
    </div>
  );
}
